/**
 * Los avisos nuevos de la campana (Etapa 11, parte B): `/api/avisos`. En pantalla se llaman «Notificaciones» (ruta `/notificaciones`); «aviso» es solo el nombre técnico.
 * No confundir con los comunicados del módulo antiguo (`/api/notificaciones`, `core/models/notificacion.model.ts`) ni con `AvisoService` (el aviso global de la pantalla).
 * El contrato está en `docs/obra/contrato-etapa11-notificaciones.md`.
 */

export type SeveridadAviso = 'info' | 'aviso' | 'urgente';
export type EstadoAviso = 'nueva' | 'leida' | 'archivada' | 'resuelta';

/** Lo que se le puede pedir a la lista: `activas` (ni archivadas ni resueltas) es el valor por omisión del servidor. */
export type FiltroEstadoAviso = 'activas' | 'nuevas' | 'leidas' | 'archivadas' | 'resueltas' | 'todas';

export interface AvisoPortal {
  id: number;
  tipo: string;
  severidad: SeveridadAviso;
  /** Texto plano: se muestra SIEMPRE como texto (interpolación de Angular), nunca como HTML. */
  titulo: string;
  texto: string;
  /** La ruta del portal a la que lleva, tal cual la mandó el servidor. Nunca se navega con ella sin pasar por `rutaDestinoSegura`. */
  ruta: string | null;
  entidad: { tipo: string; id: number } | null;
  obraId: number | null;
  empresaId: number | null;
  /** Solo en los avisos agrupados («88 productos esperan revisión en el catálogo»). */
  cantidad: number | null;
  estado: EstadoAviso;
  leida: boolean;
  creadoEn: string;
  actualizadoEn: string;
  leidoEn: string | null;
  archivadoEn: string | null;
  resueltoEn: string | null;
}

export interface PaginaAvisos {
  items: AvisoPortal[];
  /** El cursor de la página siguiente (opaco: no se arma ni se toca); null cuando no hay más. */
  siguiente: string | null;
  limite: number;
}

export interface ConteoAvisos {
  noLeidas: number;
  hayUrgentes: boolean;
}

export interface FiltrosAvisos {
  estado?: FiltroEstadoAviso;
  tipo?: string;
  limite?: number;
  cursor?: string | null;
}

/** Los tipos que ofrece el filtro, agrupados por tema, con el nombre que ve la persona. Un tipo nuevo del servidor que no esté aquí se muestra como «Aviso». */
export interface TipoDeAviso { id: string; etiqueta: string; grupo: string; icono: string }

export const TIPOS_AVISO: readonly TipoDeAviso[] = [
  { id: 'traspaso_solicitado', etiqueta: 'Traspaso por autorizar', grupo: 'Traspasos', icono: 'pi-truck' },
  { id: 'traspaso_despachado', etiqueta: 'Traspaso despachado', grupo: 'Traspasos', icono: 'pi-truck' },
  { id: 'traspaso_con_diferencia', etiqueta: 'Traspaso con diferencia', grupo: 'Traspasos', icono: 'pi-truck' },
  { id: 'arriendo_solicitud_nueva', etiqueta: 'Solicitud de arriendo nueva', grupo: 'Arriendos', icono: 'pi-key' },
  { id: 'arriendo_solicitud_aprobada', etiqueta: 'Solicitud de arriendo aprobada', grupo: 'Arriendos', icono: 'pi-key' },
  { id: 'arriendo_solicitud_rechazada', etiqueta: 'Solicitud de arriendo rechazada', grupo: 'Arriendos', icono: 'pi-key' },
  { id: 'arriendo_por_vencer', etiqueta: 'Arriendo por vencer', grupo: 'Arriendos', icono: 'pi-key' },
  { id: 'arriendo_sin_respuesta', etiqueta: 'Solicitud de arriendo sin respuesta', grupo: 'Arriendos', icono: 'pi-key' },
  { id: 'cobro_emitido', etiqueta: 'Cobro por validar', grupo: 'Cobros', icono: 'pi-receipt' },
  { id: 'cobro_aprobado', etiqueta: 'Cobro aprobado', grupo: 'Cobros', icono: 'pi-receipt' },
  { id: 'cobro_rechazado', etiqueta: 'Cobro rechazado', grupo: 'Cobros', icono: 'pi-receipt' },
  { id: 'consumos_pendientes_de_cobro', etiqueta: 'Consumos pendientes de cobro', grupo: 'Cobros', icono: 'pi-receipt' },
  { id: 'epp_vale_por_visar', etiqueta: 'Vale de EPP por visar', grupo: 'EPP', icono: 'pi-shield' },
  { id: 'epp_vale_urgente', etiqueta: 'Vale de EPP urgente', grupo: 'EPP', icono: 'pi-shield' },
  { id: 'epp_urgente_sin_visar', etiqueta: 'Vale de EPP urgente sin visar', grupo: 'EPP', icono: 'pi-shield' },
  { id: 'trabajador_contrato_epp', etiqueta: 'Contrato por terminar con EPP por devolver', grupo: 'EPP', icono: 'pi-shield' },
  { id: 'inventario_programado', etiqueta: 'Inventario programado', grupo: 'Inventario físico', icono: 'pi-clipboard' },
  { id: 'inventario_asignado', etiqueta: 'Inventario para contar', grupo: 'Inventario físico', icono: 'pi-clipboard' },
  { id: 'inventario_por_aprobar', etiqueta: 'Inventario por aprobar', grupo: 'Inventario físico', icono: 'pi-clipboard' },
  { id: 'inventario_programado_hoy', etiqueta: 'Inventario programado para hoy', grupo: 'Inventario físico', icono: 'pi-clipboard' },
  { id: 'inventario_sugerido', etiqueta: 'Inventario sugerido', grupo: 'Inventario físico', icono: 'pi-clipboard' },
  { id: 'unidad_documento_vencimiento', etiqueta: 'Documento de una unidad', grupo: 'Unidades y personas', icono: 'pi-file' },
  { id: 'unidad_mantencion_vencimiento', etiqueta: 'Mantención de una unidad', grupo: 'Unidades y personas', icono: 'pi-wrench' },
  { id: 'trabajador_documento_vencimiento', etiqueta: 'Licencia o certificación', grupo: 'Unidades y personas', icono: 'pi-id-card' },
  { id: 'trabajador_alerta_obra', etiqueta: 'Aviso de la obra sobre un trabajador', grupo: 'Unidades y personas', icono: 'pi-flag' },
  { id: 'horometro_sin_lectura', etiqueta: 'Horómetro sin lectura', grupo: 'Unidades y personas', icono: 'pi-gauge' },
  { id: 'stock_bajo_minimo', etiqueta: 'Stock bajo el mínimo', grupo: 'Bodega y catálogo', icono: 'pi-box' },
  { id: 'catalogo_pendiente_revision', etiqueta: 'Catálogo por revisar', grupo: 'Bodega y catálogo', icono: 'pi-book' },
];

export const GRUPOS_TIPO_AVISO: readonly string[] = Array.from(new Set(TIPOS_AVISO.map(t => t.grupo)));

const TIPO_POR_ID = new Map(TIPOS_AVISO.map(t => [t.id, t]));
const TIPO_DESCONOCIDO: TipoDeAviso = { id: '', etiqueta: 'Aviso', grupo: 'Otros', icono: 'pi-bell' };

export const tipoDeAviso = (id: string): TipoDeAviso => TIPO_POR_ID.get(id) ?? TIPO_DESCONOCIDO;

export const ETIQUETA_SEVERIDAD: Record<SeveridadAviso, string> = { info: 'Informativo', aviso: 'Aviso', urgente: 'Urgente' };
export const ICONO_SEVERIDAD: Record<SeveridadAviso, string> = { info: 'pi-info-circle', aviso: 'pi-bell', urgente: 'pi-exclamation-triangle' };

export const FILTROS_ESTADO_AVISO: readonly { id: FiltroEstadoAviso; etiqueta: string }[] = [
  { id: 'activas', etiqueta: 'Activas' },
  { id: 'nuevas', etiqueta: 'Sin leer' },
  { id: 'leidas', etiqueta: 'Leídas' },
  { id: 'archivadas', etiqueta: 'Archivadas' },
  { id: 'resueltas', etiqueta: 'Resueltas' },
  { id: 'todas', etiqueta: 'Todas' },
];

const texto = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const textoONulo = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const numeroONulo = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Deja un aviso del servidor en la forma del modelo, sin confiar en ella: un campo que falta o viene con otro tipo se corrige (la severidad desconocida es «info») y nada
 * se interpreta como HTML. La ruta se guarda tal cual: se valida al navegar.
 */
export function normalizarAviso(bruto: unknown): AvisoPortal | null {
  const a = bruto as Record<string, unknown> | null;
  const id = a?.['id'];
  if (!a || typeof id !== 'number' || !Number.isFinite(id)) return null;
  const sev = a['severidad'];
  const severidad: SeveridadAviso = sev === 'urgente' || sev === 'aviso' ? sev : 'info';
  const est = a['estado'];
  const estado: EstadoAviso = est === 'leida' || est === 'archivada' || est === 'resuelta' ? est : 'nueva';
  const ent = a['entidad'] as { tipo?: unknown; id?: unknown } | null | undefined;
  return {
    id,
    tipo: texto(a['tipo']),
    severidad,
    titulo: texto(a['titulo']),
    texto: texto(a['texto']),
    ruta: textoONulo(a['ruta']),
    entidad: ent && typeof ent.id === 'number' ? { tipo: texto(ent.tipo), id: ent.id } : null,
    obraId: numeroONulo(a['obraId']),
    empresaId: numeroONulo(a['empresaId']),
    cantidad: numeroONulo(a['cantidad']),
    estado,
    leida: a['leida'] === true || estado !== 'nueva',
    creadoEn: texto(a['creadoEn']),
    actualizadoEn: texto(a['actualizadoEn'] ?? a['creadoEn']),
    leidoEn: textoONulo(a['leidoEn']),
    archivadoEn: textoONulo(a['archivadoEn']),
    resueltoEn: textoONulo(a['resueltoEn']),
  };
}

/** ¿El aviso entra en este filtro de estado? (para sacar de la lista el que acaba de dejar de coincidir). */
export function coincideConFiltro(a: AvisoPortal, filtro: FiltroEstadoAviso): boolean {
  switch (filtro) {
    case 'todas': return true;
    case 'activas': return a.estado === 'nueva' || a.estado === 'leida';
    case 'nuevas': return a.estado === 'nueva';
    case 'leidas': return a.estado === 'leida';
    case 'archivadas': return a.estado === 'archivada';
    case 'resueltas': return a.estado === 'resuelta';
  }
}
